import { useState } from "react";
import type { LoroDoc } from "loro-crdt";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button, Checkbox, Field, Input } from "../ui";
import { useDocSelector } from "../core/useDocSelector";
import type { DocHandle } from "../sync/docManager";
import { placeKey } from "../core/ops";
import { type Place, deletePlaceList, readPlaceLists, savePlaceList } from "./settingsOps";

/**
 * Settings → Signals → Place lists (docs/plans/buttons-and-place-lists.md, part 3): several places under one name.
 * A task at "Supermarket" is on the list at each shop in it. A place with the same name wins over the list.
 */
export function PlaceLists({
  core,
  places,
  ro,
  write,
}: {
  core: DocHandle | null | undefined;
  places: Place[];
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
}) {
  const { t } = useTranslation();
  const listsJson = useDocSelector(core, (doc) => JSON.stringify(readPlaceLists(doc)));
  const lists = JSON.parse(listsJson ?? "[]") as ReturnType<typeof readPlaceLists>;
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const names = Object.fromEntries(places.map((p) => [p.id, p.name]));
  const clash = name.trim() !== "" && places.some((p) => placeKey(p.name) === placeKey(name));

  const reset = () => {
    setEditing(null);
    setName("");
    setPicked([]);
  };

  if (places.length === 0 && lists.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="place-lists">
      <h3 className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
        {t("signals.lists.title")}
      </h3>
      <p className="text-[12.5px] text-neutral-500">{t("signals.lists.hint")}</p>
      {lists.length > 0 && (
        <ul className="flex flex-col gap-1">
          {lists.map((l) => (
            <li
              key={l.id}
              className="flex flex-wrap items-center gap-2 text-[13px]"
              data-testid="place-list-row"
            >
              <span className="font-medium">{l.name}</span>
              <span className="text-neutral-500">
                {l.placeIds.length === 0
                  ? t("signals.lists.empty")
                  : l.placeIds.map((id) => names[id] ?? t("signals.privatePlace")).join(", ")}
              </span>
              {!ro && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => {
                      setEditing(l.id);
                      setName(l.name);
                      setPicked(l.placeIds.filter((id) => id in names));
                    }}
                    data-testid="place-list-change"
                  >
                    {t("signals.lists.change")}
                  </Button>
                  <button
                    type="button"
                    aria-label={t("signals.lists.delete", { name: l.name })}
                    onClick={() => {
                      write((doc) => deletePlaceList(doc, l.id));
                      if (editing === l.id) reset();
                    }}
                    className="grid h-7 w-7 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {!ro && places.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="place-list-new">
          <Field label={t("signals.lists.name")} htmlFor="place-list-name">
            <Input
              id="place-list-name"
              compact
              className="w-48"
              value={name}
              placeholder={t("signals.lists.namePlaceholder")}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          {clash && (
            <p className="text-[12px] text-neutral-500" data-testid="place-list-clash">
              {t("signals.lists.clash", { name: name.trim() })}
            </p>
          )}
          <fieldset className="flex flex-wrap gap-x-4 gap-y-1">
            <legend className="mb-1 text-[12px] text-neutral-500">{t("signals.lists.places")}</legend>
            {places.map((p) => (
              <label key={p.id} className="flex items-center gap-1.5 text-[13px]">
                <Checkbox
                  checked={picked.includes(p.id)}
                  label={p.name}
                  onChange={(on) => setPicked((cur) => (on ? [...cur, p.id] : cur.filter((x) => x !== p.id)))}
                />
                <span aria-hidden>{p.name}</span>
              </label>
            ))}
          </fieldset>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!name.trim() || picked.length === 0}
              onClick={() => {
                write((doc) => savePlaceList(doc, { id: editing ?? undefined, name, placeIds: picked }));
                reset();
              }}
              data-testid="place-list-save"
            >
              {editing ? t("signals.lists.save") : t("signals.lists.add")}
            </Button>
            {editing && (
              <Button size="sm" variant="ghost" onClick={reset}>
                {t("buttons.cancel")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
